/**
 * Chat Store - Zustand store for chat state management
 * Validates: Requirements 13.1, 13.2, 13.5, 13.6
 */
import { create } from 'zustand';
import apiClient from '../api/client';
import { ChatSession, ChatMessage, LLMProvider, ModelSettings } from '../types';

/**
 * Model information from provider
 */
export interface ModelInfo {
  id: string;
  name: string;
  provider: LLMProvider;
  contextWindow: number;
  maxOutputTokens?: number;
  supportsStreaming: boolean;
  supportsVision?: boolean;
}

/**
 * Chat session summary for list display
 */
export interface ChatSessionSummary {
  id: string;
  title?: string;
  model: string;
  provider: string;
  createdAt: string;
  updatedAt: string;
}

interface ChatState {
  // Current session
  currentSession: ChatSession | null;
  currentSessionId: string | null;
  
  // Session list
  sessions: ChatSessionSummary[];
  sessionsLoading: boolean;
  
  // Messages
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingContent: string;
  
  // Model selection
  selectedModel: string;
  selectedProvider: LLMProvider;
  availableModels: ModelInfo[];
  
  // Settings
  settings: ModelSettings;
  promptEnhancerEnabled: boolean;
  
  // Error state
  error: string | null;
  
  // Actions
  setSelectedModel: (model: string, provider: LLMProvider) => void;
  setSettings: (settings: Partial<ModelSettings>) => void;
  togglePromptEnhancer: () => void;
  
  // Session actions
  loadSessions: () => Promise<void>;
  createSession: (model: string, provider: LLMProvider) => Promise<string | null>;
  loadSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  
  // Message actions
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  clearError: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  currentSession: null,
  currentSessionId: null,
  sessions: [],
  sessionsLoading: false,
  messages: [],
  isStreaming: false,
  streamingContent: '',
  selectedModel: 'gpt-4o',
  selectedProvider: 'openai',
  availableModels: [],
  settings: {
    temperature: 0.7,
    maxTokens: 4096,
  },
  promptEnhancerEnabled: false,
  error: null,

  // Model selection
  setSelectedModel: (model, provider) => {
    set({ selectedModel: model, selectedProvider: provider });
  },

  setSettings: (newSettings) => {
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
    }));
  },

  togglePromptEnhancer: () => {
    set((state) => ({ promptEnhancerEnabled: !state.promptEnhancerEnabled }));
  },

  // Load sessions list
  loadSessions: async () => {
    set({ sessionsLoading: true, error: null });
    try {
      const response = await apiClient.get('/api/chat/sessions');
      const sessions = response.data.data || [];
      set({ sessions, sessionsLoading: false });
    } catch (error) {
      console.error('Failed to load sessions:', error);
      set({ sessionsLoading: false, error: 'Failed to load chat history' });
    }
  },

  // Create new session
  createSession: async (model, provider) => {
    set({ error: null });
    try {
      const response = await apiClient.post('/api/chat/sessions', {
        model,
        provider,
        settings: get().settings,
      });
      const session = response.data.data;
      set({
        currentSession: session,
        currentSessionId: session.id,
        messages: [],
        selectedModel: model,
        selectedProvider: provider,
      });
      // Refresh sessions list
      get().loadSessions();
      return session.id;
    } catch (error) {
      console.error('Failed to create session:', error);
      set({ error: 'Failed to create chat session' });
      return null;
    }
  },

  // Load existing session
  loadSession: async (sessionId) => {
    set({ error: null });
    try {
      const response = await apiClient.get(`/api/chat/sessions/${sessionId}`);
      const session = response.data.data;
      
      // Parse messages from session
      const messages: ChatMessage[] = session.messages || [];
      
      set({
        currentSession: session,
        currentSessionId: session.id,
        messages,
        selectedModel: session.model,
        selectedProvider: session.provider as LLMProvider,
      });
    } catch (error) {
      console.error('Failed to load session:', error);
      set({ error: 'Failed to load chat session' });
    }
  },

  // Delete session
  deleteSession: async (sessionId) => {
    try {
      await apiClient.delete(`/api/chat/sessions/${sessionId}`);
      
      // If deleting current session, clear it
      if (get().currentSessionId === sessionId) {
        set({
          currentSession: null,
          currentSessionId: null,
          messages: [],
        });
      }
      
      // Refresh sessions list
      get().loadSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
      set({ error: 'Failed to delete chat session' });
    }
  },

  // Send message with streaming support
  sendMessage: async (content) => {
    const state = get();
    const { currentSessionId, selectedModel, selectedProvider, settings, promptEnhancerEnabled } = state;
    
    // Create session if none exists
    let sessionId = currentSessionId;
    if (!sessionId) {
      sessionId = await get().createSession(selectedModel, selectedProvider);
      if (!sessionId) return;
    }

    // Add user message to state
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    
    set((state) => ({
      messages: [...state.messages, userMessage],
      isStreaming: true,
      streamingContent: '',
      error: null,
    }));

    try {
      // Enhance prompt if enabled
      let finalContent = content;
      if (promptEnhancerEnabled) {
        try {
          const enhanceResponse = await apiClient.post('/api/enhance', {
            prompt: content,
            target: 'text',
            model: selectedModel,
            provider: selectedProvider,
          });
          finalContent = enhanceResponse.data.enhanced || content;
        } catch {
          // Continue with original prompt if enhancement fails
          console.warn('Prompt enhancement failed, using original');
        }
      }

      // Save user message to session
      await apiClient.post(`/api/chat/sessions/${sessionId}/messages`, {
        role: 'user',
        content,
      });

      // Get all messages for context
      const messagesForApi = get().messages.map((m) => ({
        role: m.role,
        content: m.role === 'user' && m.content === content ? finalContent : m.content,
      }));

      /**
       * Chat completion with streaming support using native fetch API
       *
       * NOTE: We use fetch() instead of apiClient for streaming responses because:
       *
       * 1. Native fetch provides better SSE (Server-Sent Events) support via ReadableStream
       *    - ReadableStream allows processing chunks as they arrive
       *    - Axios doesn't handle streaming responses cleanly
       *
       * 2. Manual CSRF handling is required but follows same pattern as apiClient
       *    - getCsrfToken() matches the implementation in api/client.ts
       *    - Cookie is automatically included via credentials: 'include'
       *
       * 3. This bypasses apiClient interceptors, which means:
       *    - Session expiration (401) won't trigger automatic redirect
       *    - Errors must be manually caught in the stream processor
       *    - Response logging is not automatic
       *
       * IMPORTANT: If session expires during streaming:
       * - The stream will fail with HTTP error
       * - Error is caught in try/catch below and shown to user
       * - User must manually refresh or will be redirected on next API call
       *
       * @see getCsrfToken() below - Matches apiClient CSRF logic
       * @see packages/frontend/src/api/client.ts - Reference implementation
       */
      const response = await fetch('/bff/api/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken() || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          model: selectedModel,
          provider: selectedProvider,
          messages: messagesForApi,
          stream: true,
          settings,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Process SSE stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith('data: ')) {
            const data = trimmed.slice(6);
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.delta) {
                fullContent += parsed.delta;
                set({ streamingContent: fullContent });
              }
              if (parsed.error) {
                throw new Error(parsed.message || 'Stream error');
              }
            } catch (e) {
              if (e instanceof SyntaxError) continue;
              throw e;
            }
          }
        }
      }

      // Add assistant message
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: fullContent,
        timestamp: new Date(),
      };

      set((state) => ({
        messages: [...state.messages, assistantMessage],
        isStreaming: false,
        streamingContent: '',
      }));

      // Save assistant message to session
      await apiClient.post(`/api/chat/sessions/${sessionId}/messages`, {
        role: 'assistant',
        content: fullContent,
      });

      // Update session title if first message
      if (get().messages.length === 2) {
        const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
        await apiClient.patch(`/api/chat/sessions/${sessionId}`, { title });
        get().loadSessions();
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      set({
        isStreaming: false,
        streamingContent: '',
        error: error instanceof Error ? error.message : 'Failed to send message',
      });
    }
  },

  clearMessages: () => {
    set({
      currentSession: null,
      currentSessionId: null,
      messages: [],
      streamingContent: '',
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Helper to get CSRF token
function getCsrfToken(): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split('; __Host-csrf=');
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() ?? null;
  }
  return null;
}
